import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronsLeft, ChevronsRight, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { visibleNav } from "./navItems";
import { BrandIcon } from "@/components/bakery/Art";
import { cn } from "@/lib/utils";

function Item({ item, collapsed, nested }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/" || item.to === "/kasir"}
      title={collapsed ? item.label : undefined}
      data-testid={`nav-${item.key}`}
      className={({ isActive }) =>
        cn(
          "press group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-zinc-300 hover:bg-white/5 hover:text-white",
          nested && !collapsed && "pl-9",
          isActive && "bg-white/10 font-semibold text-white"
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-brand" />}
          <Icon className={cn("h-[18px] w-[18px] shrink-0", isActive ? "text-brand" : "text-zinc-400 group-hover:text-white")} strokeWidth={1.8} />
          {!collapsed && (
            <span className="min-w-0 flex-1 truncate">
              {item.label}
              {item.sub && <span className="ml-1.5 text-[11px] font-normal text-zinc-500">{item.sub}</span>}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function Group({ group, collapsed }) {
  const { pathname } = useLocation();
  const activeChild = group.children.some((c) => pathname === c.to || pathname.startsWith(`${c.to}/`));
  const [open, setOpen] = useState(true);
  const Icon = group.icon;
  if (collapsed) return group.children.map((c) => <Item key={c.key} item={c} collapsed />);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`nav-group-${group.key}`}
        className={cn("press flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-zinc-300 hover:bg-white/5 hover:text-white", activeChild && "text-white")}
      >
        <Icon className="h-[18px] w-[18px] text-zinc-400" strokeWidth={1.8} />
        <span className="flex-1 text-left font-medium">{group.label}</span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="mt-0.5 space-y-0.5">{group.children.map((c) => <Item key={c.key} item={c} nested />)}</div>}
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  return (
    <aside
      data-testid="sidebar"
      className={cn("grain relative sticky top-0 hidden h-screen shrink-0 flex-col bg-ink text-white md:flex", collapsed ? "w-[76px]" : "w-[264px]")}
      style={{ transition: "width 220ms ease" }}
    >
      <div className={cn("flex items-center gap-3 border-b border-white/10 px-4 py-5", collapsed && "justify-center px-2")}>
        <BrandIcon className="h-10 w-auto shrink-0" />
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-display text-lg font-extrabold tracking-tight">STRONGHOLD</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400">Stock · Gudang Pusat</div>
          </div>
        )}
      </div>
      <nav className="no-scrollbar flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {visibleNav(user).map((n) => (n.children ? <Group key={n.key} group={n} collapsed={collapsed} /> : <Item key={n.key} item={n} collapsed={collapsed} />))}
      </nav>
      <div className="border-t border-white/10 p-3">
        {!collapsed && (
          <div className="mb-2 rounded-md bg-white/5 px-3 py-2" data-testid="sidebar-user">
            <div className="truncate text-sm font-semibold">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-400">{user?.role === "admin" ? "Admin" : "Staff"}</div>
          </div>
        )}
        <div className={cn("flex gap-2", collapsed && "flex-col")}>
          <button onClick={logout} data-testid="logout-btn" title="Keluar"
            className="press flex flex-1 items-center justify-center gap-2 rounded-md border border-white/15 py-2 text-xs text-zinc-300 hover:border-brand hover:text-white">
            <LogOut className="h-4 w-4" /> {!collapsed && "Keluar"}
          </button>
          <button onClick={onToggle} data-testid="sidebar-collapse-btn" title="Ciutkan menu"
            className="press grid place-items-center rounded-md border border-white/15 px-2.5 py-2 text-zinc-300 hover:text-white">
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </aside>
  );
}
